import { service } from '@ember/service';
import Component from '@glimmer/component';

import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { trackedFunction } from 'reactiveweb/function';

import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

interface SelectedAttributesIndicatorSignature {
  Args: {
    model: TODO;
    attributes: string[];
    tooltip?: string;
    tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  };
  Element: HTMLDivElement;
}

export class SelectedAttributesIndicator extends Component<SelectedAttributesIndicatorSignature> {
  // Services
  @service declare recordRelationships: TODO;
  @service declare store: TODO;

  // Getter and setter
  get tooltipSide() {
    return this.args.tooltipSide ?? 'top';
  }

  get hasSelectedAttributes() {
    return this.checkSelectedAttributes.value ?? false;
  }

  // Functions
  checkSelectedAttributes = trackedFunction(this, async () => {
    const { model, attributes } = this.args;

    this.recordRelationships.revisionFor(model);

    const values = await Promise.all(
      attributes.map(async (path) => {
        const fields = this.store.schema.fields({
          type: getRecordType(model),
        });
        const fieldKind = fields.get(path)?.kind;

        if (fieldKind === 'hasMany') {
          const reference = model.hasMany(path);
          await reference.load();
          const refValue = reference.value();
          return refValue ? refValue.length > 0 : false;
        } else if (fieldKind === 'belongsTo') {
          const reference = model.belongsTo(path);
          await reference.load();
          return !!reference.value();
        }

        return path.split('.').reduce((acc, key) => acc?.[key], model);
      }),
    );

    return values.filter(Boolean).length > 0;
  });

  // Template
  <template>
    {{#if this.hasSelectedAttributes}}
      <div class={{(bem)}} data-test-selected-attributes-indicator>
        {{svgJar 'selected-attributes' class=(bem 'icon')}}

        {{#if @tooltip}}
          <EmberTooltip @text={{@tooltip}} @side={{this.tooltipSide}} />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
